/**
 * Engagement Tracker Utility
 *
 * Tracks user engagement actions for surprise badges and shows
 * a toast notification immediately when a badge is earned.
 */
import Toast from 'react-native-root-toast';
import { apiPost } from './api';
import { getRandomEncouragingMessage } from './surpriseBadgeConfig';
import { t } from '../i18n';

/**
 * Track an engagement action and show a toast if a badge was earned
 *
 * @param {string} triggerType - The engagement trigger type
 * @param {string} organization - Optional organization (required for new users)
 * @returns {Promise<Object|null>} The tracking result or null on error
 */
export async function trackEngagement(triggerType, organization = null) {
  try {
    const body = { triggerType };
    if (organization) {
      body.organization = organization;
    }

    const result = await apiPost('Resty', '/items/achievements/engagement', { body });

    // Show toast if a new badge was earned
    if (result.earnedNewBadge) {
      showBadgeEarnedToast(result.surpriseBadgeCount);
    }

    return result;
  } catch (error) {
    // Silently ignore tracking errors - don't disrupt user flow
    console.log('Engagement tracking failed:', error.message);
    return null;
  }
}

/**
 * Show a toast notification for earning a badge
 *
 * @param {number} badgeCount - Total badge count after earning
 */
function showBadgeEarnedToast(badgeCount) {
  const message = getRandomEncouragingMessage();

  Toast.show(`🎉 ${t('badges:bonusEarned')}\n${message}`, {
    duration: Toast.durations.LONG,
    position: Toast.positions.TOP,
    shadow: true,
    animation: true,
    hideOnPress: true,
    backgroundColor: '#8B5CF6', // Purple for surprise badges
    textColor: '#FFFFFF',
    opacity: 1,
    containerStyle: {
      paddingHorizontal: 20,
      paddingVertical: 15,
      borderRadius: 12,
      marginTop: 40,
    },
  });
}

/**
 * Fire-and-forget engagement tracking (for non-blocking use)
 * Use this when you don't need to wait for the result
 *
 * @param {string} triggerType - The engagement trigger type
 * @param {string} organization - Optional organization
 */
export function trackEngagementAsync(triggerType, organization = null) {
  trackEngagement(triggerType, organization).catch(() => {
    // Already handled in trackEngagement
  });
}
